import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EduShell, EmptyState, TEACHER_NAV } from "@/components/EduShell";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { eduRpc, GRADE_LEVELS, SUBJECTS } from "@/lib/education";
import { teacherAiDraftFn } from "@/lib/teacher-ai.functions";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/teacher/ai")({
  component: TeacherAi,
  head: () => ({
    meta: [
      { title: "AI assistant for teachers - Inquira" },
      {
        name: "description",
        content:
          "Draft quizzes, rubrics, study guides and essay prompts with AI, review every line, then publish to your class.",
      },
      { property: "og:title", content: "Inquira AI assistant for teachers" },
      { property: "og:description", content: "AI drafts classroom material; you approve it before students see it." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

type Kind = "quiz" | "rubric" | "study_guide" | "essay";

const KINDS: Array<{ value: Kind; label: string; hint: string }> = [
  { value: "quiz", label: "Quiz", hint: "Questions with an answer key" },
  { value: "rubric", label: "Rubric", hint: "Grading criteria and points" },
  { value: "study_guide", label: "Study guide", hint: "Sections students revise from" },
  { value: "essay", label: "Essay prompt", hint: "Prompt, expectations and rubric" },
];

interface QuestionDraft {
  prompt: string;
  kind: string;
  options?: string[];
  answer?: string;
  points?: number;
}
interface SectionDraft { heading: string; body: string }
interface CriterionDraft { name: string; points?: number; descriptors?: string[] }
interface DraftContent {
  title?: string;
  summary?: string;
  instructions?: string;
  questions?: QuestionDraft[];
  sections?: SectionDraft[];
  criteria?: CriterionDraft[];
}

interface DraftRow {
  id: string;
  kind: string;
  title: string;
  content: DraftContent;
  status: string;
  created_at: string;
}

function sectionsToText(d: DraftContent) {
  const parts: string[] = [];
  if (d.instructions) parts.push(d.instructions);
  for (const s of d.sections ?? []) parts.push(`${s.heading}\n${s.body}`);
  for (const c of d.criteria ?? []) {
    parts.push(`${c.name} (${c.points ?? 0} pts)\n${(c.descriptors ?? []).join("\n")}`);
  }
  return parts.join("\n\n").trim();
}

function TeacherAi() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState<Array<{ id: string; name: string }>>([]);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState<string | null>(null);
  const [form, setForm] = useState({
    kind: "quiz" as Kind,
    topic: "",
    subject: "",
    gradeLevel: "",
    itemCount: 5,
    notes: "",
    classId: "",
  });

  async function loadDrafts() {
    const { data } = await supabase
      .from("edu_ai_drafts")
      .select("id, kind, title, content, status, created_at")
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    setDrafts(((data as unknown as DraftRow[]) ?? []).map((d) => ({ ...d, content: (d.content ?? {}) as DraftContent })));
  }

  useEffect(() => {
    if (!user) return;
    void supabase
      .from("classes")
      .select("id, name")
      .eq("teacher_id", user.id)
      .eq("archived", false)
      .order("name")
      .then(({ data }) => {
        const list = (data as Array<{ id: string; name: string }>) ?? [];
        setClasses(list);
        setForm((f) => ({ ...f, classId: f.classId || (list[0]?.id ?? "") }));
      });
    void loadDrafts();
  }, [user]);

  async function generate() {
    if (form.topic.trim().length < 3) {
      toast.error(i18n.t("edu.teacher_ai.tell_the_assistant_what_the_material_sho"));
      return;
    }
    setBusy(true);
    try {
      const out = await teacherAiDraftFn({
        data: {
          kind: form.kind,
          topic: form.topic.trim(),
          subject: form.subject || undefined,
          gradeLevel: form.gradeLevel || undefined,
          itemCount: form.itemCount,
          notes: form.notes || undefined,
        },
      });
      const content = out.content as DraftContent;
      const { error } = await eduRpc<string>("edu_save_ai_draft", {
        _kind: form.kind,
        _title: content.title ?? form.topic.trim(),
        _content: content,
        _class_id: form.classId || null,
        _prompt: form.topic.trim(),
      });
      if (error) throw new Error(error);
      toast.success(i18n.t("edu.teacher_ai.draft_ready_for_your_review"));
      setForm((f) => ({ ...f, topic: "", notes: "" }));
      await loadDrafts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not create a draft");
    } finally {
      setBusy(false);
    }
  }

  async function discard(id: string) {
    const { error } = await eduRpc("edu_discard_ai_draft", { _draft_id: id });
    if (error) { toast.error(error); return; }
    setDrafts((d) => d.filter((x) => x.id !== id));
  }

  async function publish(draft: DraftRow, status: "draft" | "published") {
    if (!form.classId) { toast.error(i18n.t("edu.teacher_ai.pick_a_class_to_publish_into")); return; }
    setPublishing(draft.id);
    const content = draft.content;
    const kindMap: Record<string, string> = {
      quiz: "quiz",
      essay: "essay",
      study_guide: "study_guide",
      rubric: "homework",
    };
    const questions = (content.questions ?? []).map((q) => ({
      prompt: q.prompt,
      kind: q.kind || "short_answer",
      options: q.options ?? [],
      answer_key: q.answer ?? null,
      points: q.points ?? 1,
    }));
    const points =
      questions.length > 0
        ? questions.reduce((sum, q) => sum + Number(q.points || 0), 0)
        : (content.criteria ?? []).reduce((sum, c) => sum + Number(c.points ?? 0), 0) || 100;

    const { data, error } = await eduRpc<string>("edu_save_assignment", {
      _class_id: form.classId,
      _title: draft.title,
      _instructions: sectionsToText(content) || content.summary || null,
      _kind: kindMap[draft.kind] ?? "homework",
      _subject: form.subject || null,
      _grade_level: form.gradeLevel || null,
      _points: points,
      _ai_mode: draft.kind === "quiz" ? "disabled" : "guided",
      _status: status,
      _rubric: content.criteria ? { criteria: content.criteria } : null,
      _questions: questions,
    });
    setPublishing(null);
    if (error || !data) { toast.error(error ?? "Could not publish"); return; }
    await eduRpc("edu_mark_draft_published", { _draft_id: draft.id, _assignment_id: data });
    toast.success(status === "published" ? "Published to your class" : "Saved as an assignment draft");
    void navigate({ to: "/teacher/assignments/$assignmentId", params: { assignmentId: data } });
  }

  return (
    <EduShell
      nav={TEACHER_NAV}
      title={i18n.t("edu.teacher_ai.ai_assistant")}
      subtitle={i18n.t("edu.teacher_ai.ask_for_a_quiz_rubric_study_guide_or_ess")}
    >
      <div className="space-y-4 rounded-2xl border border-rule bg-card p-4">
        <div className="grid gap-2 sm:grid-cols-4">
          {KINDS.map((k) => (
            <button
              key={k.value}
              onClick={() => setForm({ ...form, kind: k.value })}
              className={`rounded-xl border p-3 text-left text-sm ${
                form.kind === k.value ? "border-primary bg-primary/10" : "border-rule"
              }`}
            >
              <span className="block font-semibold text-ink">{k.label}</span>
              <span className="text-xs text-ink-muted">{k.hint}</span>
            </button>
          ))}
        </div>

        <textarea
          className="w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
          rows={3}
          placeholder={i18n.t("edu.teacher_ai.what_should_it_cover_e_g_solving_two_ste")}
          value={form.topic}
          onChange={(e) => setForm({ ...form, topic: e.target.value })}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <select
            className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={form.classId}
            onChange={(e) => setForm({ ...form, classId: e.target.value })}
          >
            <option value=""><T k="edu.teacher_ai.no_class_yet" /></option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
          >
            <option value=""><T k="edu.teacher_ai.subject" /></option>
            {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            className="rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
            value={form.gradeLevel}
            onChange={(e) => setForm({ ...form, gradeLevel: e.target.value })}
          >
            <option value=""><T k="edu.teacher_ai.level" /></option>
            {GRADE_LEVELS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            How many items
            <input
              type="number"
              min={1}
              max={15}
              className="w-20 rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
              value={form.itemCount}
              onChange={(e) => setForm({ ...form, itemCount: Number(e.target.value) })}
            />
          </label>
        </div>

        <textarea
          className="w-full rounded-xl border border-rule bg-background px-3 py-2 text-sm text-ink"
          rows={2}
          placeholder={i18n.t("edu.teacher_ai.anything_else_reading_level_standards_wh")}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />

        <Button onClick={generate} disabled={busy}>{busy ? "Drafting…" : "Draft it"}</Button>
      </div>

      <h2 className="mb-3 mt-8 font-display text-lg font-bold text-ink"><T k="edu.teacher_ai.waiting_for_your_approval" /></h2>
      {drafts.length === 0 ? (
        <EmptyState title={i18n.t("edu.teacher_ai.no_drafts_yet")} body="Anything the assistant writes lands here first." />
      ) : (
        <ul className="space-y-3">
          {drafts.map((d) => (
            <li key={d.id} className="rounded-2xl border border-rule bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-ink">{d.title}</p>
                  <p className="text-xs uppercase tracking-wide text-ink-muted">{d.kind.replace(/_/g, " ")}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" disabled={publishing === d.id} onClick={() => publish(d, "published")}>
                    Approve &amp; publish
                  </Button>
                  <Button size="sm" variant="outline" disabled={publishing === d.id} onClick={() => publish(d, "draft")}>
                    Save as draft
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => discard(d.id)}><T k="edu.teacher_ai.discard" /></Button>
                </div>
              </div>

              {d.content.summary && <p className="mt-2 text-sm text-ink-muted">{d.content.summary}</p>}

              {(d.content.questions ?? []).length > 0 && (
                <ol className="mt-3 space-y-2 text-sm">
                  {(d.content.questions ?? []).map((q, i) => (
                    <li key={i} className="rounded-xl border border-rule p-3">
                      <p className="font-medium text-ink">{i + 1}. {q.prompt}</p>
                      {(q.options ?? []).length > 0 && (
                        <ul className="mt-1 list-disc pl-5 text-ink-muted">
                          {(q.options ?? []).map((o, j) => <li key={j}>{o}</li>)}
                        </ul>
                      )}
                      <p className="mt-1 text-xs text-ink-muted">
                        {[q.answer ? `Answer: ${q.answer}` : null, `${q.points ?? 1} pts`].filter(Boolean).join(" · ")}
                      </p>
                    </li>
                  ))}
                </ol>
              )}

              {(d.content.sections ?? []).length > 0 && (
                <div className="mt-3 space-y-2 text-sm">
                  {(d.content.sections ?? []).map((s, i) => (
                    <div key={i} className="rounded-xl border border-rule p-3">
                      <p className="font-semibold text-ink">{s.heading}</p>
                      <p className="whitespace-pre-line text-ink-muted">{s.body}</p>
                    </div>
                  ))}
                </div>
              )}

              {(d.content.criteria ?? []).length > 0 && (
                <div className="mt-3 space-y-2 text-sm">
                  {(d.content.criteria ?? []).map((c, i) => (
                    <div key={i} className="rounded-xl border border-rule p-3">
                      <p className="font-semibold text-ink">{c.name} · {c.points ?? 0} pts</p>
                      {(c.descriptors ?? []).length > 0 && (
                        <ul className="mt-1 list-disc pl-5 text-ink-muted">
                          {(c.descriptors ?? []).map((x, j) => <li key={j}>{x}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </EduShell>
  );
}
